import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { reactive } from "vue"
import { PrivacyLevel } from "@webapp/gp/GroupAdminModel"

export interface Comment {
    id: string
    itemId: string
    authorName: string
    text: string
    timestamp: DateTime
    privacyLevel: PrivacyLevel
    likeCount: number
    likedByMe: boolean
}

let nextId = 1
function generateId() {
    return `comment-${nextId++}-${Date.now()}`
}

export const useCommentStore = defineStore("commentStore", () => {
    const contentComments = reactive(new Map<string, Comment[]>())
    let seeded = false

    function getComments(itemId: string): Comment[] {
        return contentComments.get(itemId) ?? []
    }

    function getCommentCount(itemId: string): number {
        return contentComments.get(itemId)?.length ?? 0
    }

    function addComment(itemId: string, text: string, privacyLevel = PrivacyLevel.Everyone): void {
        if (!contentComments.has(itemId)) {
            contentComments.set(itemId, [])
        }
        contentComments.get(itemId)!.push({
            id: generateId(),
            itemId,
            authorName: 'You',
            text,
            timestamp: DateTime.now(),
            privacyLevel,
            likeCount: 0,
            likedByMe: false,
        })
    }

    function toggleCommentLike(commentId: string): void {
        for (const comments of contentComments.values()) {
            const comment = comments.find(c => c.id === commentId)
            if (comment) {
                if (comment.likedByMe) {
                    comment.likedByMe = false
                    comment.likeCount--
                } else {
                    comment.likedByMe = true
                    comment.likeCount++
                }
                return
            }
        }
    }

    function ensureSeeded(itemIds: string[]): void {
        if (seeded) {
            return
        }
        seeded = true

        const seedIds = itemIds.slice(0, 4)
        const privacyLevels = [PrivacyLevel.Everyone, PrivacyLevel.Friends, PrivacyLevel.Groups, PrivacyLevel.Everyone]

        const seedComments: Array<{ authorName: string; text: string; daysAgo: number; likeCount: number; likedByMe: boolean }> = [
            { authorName: 'Sarah M.', text: 'Great photo of grandma!', daysAgo: 12, likeCount: 3, likedByMe: true },
            { authorName: 'Uncle Bob', text: 'I remember this day so well.', daysAgo: 10, likeCount: 1, likedByMe: false },
            { authorName: 'Cousin Pat', text: 'What year was this taken?', daysAgo: 9, likeCount: 0, likedByMe: false },
            { authorName: 'Sarah M.', text: 'Love finding these old pictures.', daysAgo: 8, likeCount: 2, likedByMe: false },
            { authorName: 'Uncle Bob', text: 'Look how young everyone was!', daysAgo: 7, likeCount: 4, likedByMe: true },
            { authorName: 'Cousin Pat', text: 'Does anyone know where this was?', daysAgo: 6, likeCount: 0, likedByMe: false },
            { authorName: 'Sarah M.', text: "This must have been at grandpa's farm.", daysAgo: 5, likeCount: 5, likedByMe: true },
            { authorName: 'Uncle Bob', text: 'We should print this one out.', daysAgo: 4, likeCount: 1, likedByMe: false },
            { authorName: 'Cousin Pat', text: 'Mom would love to see this!', daysAgo: 3, likeCount: 2, likedByMe: false },
            { authorName: 'Aunt Linda', text: 'He looks just like dad in this one.', daysAgo: 2, likeCount: 3, likedByMe: true },
            { authorName: 'Sarah M.', text: 'I wonder if we have more from this set.', daysAgo: 1, likeCount: 0, likedByMe: false },
            { authorName: 'Uncle Bob', text: 'That suit was all the rage back then!', daysAgo: 1, likeCount: 1, likedByMe: false },
        ]

        seedIds.forEach((itemId, index) => {
            const commentCount = index === 0 ? 10 : 1 + index % 3
            const startIndex = (index * 3) % seedComments.length
            const comments: Comment[] = []
            for (let i = 0; i < commentCount; i++) {
                const seed = seedComments[(startIndex + i) % seedComments.length]
                comments.push({
                    id: generateId(),
                    itemId,
                    authorName: seed.authorName,
                    text: seed.text,
                    timestamp: DateTime.now().minus({ days: seed.daysAgo }),
                    privacyLevel: privacyLevels[i % privacyLevels.length],
                    likeCount: seed.likeCount,
                    likedByMe: seed.likedByMe,
                })
            }
            contentComments.set(itemId, comments)
        })
    }

    return {
        contentComments,
        getComments,
        getCommentCount,
        addComment,
        toggleCommentLike,
        ensureSeeded,
    }
})
